// src/components/QuickMessageDialog/index.js
//
// Modal de resposta rápida no padrão "Aeros Dark" — layout do mockup
// "Tela Aeros Dark - Respostas Rápidas" (formulário à esquerda, pré-visualização
// no chat à direita). A lógica de dados (salvar, upload de arquivo/áudio,
// remover mídia, templates oficiais) é a mesma da versão anterior.

import React, { useContext, useState, useEffect, useRef, useMemo } from "react";

import * as Yup from "yup";
import { Formik, Form, Field, getIn } from "formik";
import { toast } from "react-toastify";
import "emoji-mart/css/emoji-mart.css";
import { Picker } from "emoji-mart";

import { makeStyles, alpha } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import CircularProgress from "@material-ui/core/CircularProgress";
import IconButton from "@material-ui/core/IconButton";
import Switch from "@material-ui/core/Switch";
import InputAdornment from "@material-ui/core/InputAdornment";
import ClickAwayListener from "@material-ui/core/ClickAwayListener";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import CloseIcon from "@material-ui/icons/Close";
import MoodIcon from "@material-ui/icons/Mood";
import CodeIcon from "@material-ui/icons/Code";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import MicIcon from "@material-ui/icons/Mic";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import GroupOutlinedIcon from "@material-ui/icons/GroupOutlined";
import FlashOnIcon from "@material-ui/icons/FlashOn";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import DoneAllIcon from "@material-ui/icons/DoneAll";
import ImageOutlinedIcon from "@material-ui/icons/ImageOutlined";
import MicNoneIcon from "@material-ui/icons/MicNone";
import VideocamOutlinedIcon from "@material-ui/icons/VideocamOutlined";
import PictureAsPdfOutlinedIcon from "@material-ui/icons/PictureAsPdfOutlined";
import InsertDriveFileOutlinedIcon from "@material-ui/icons/InsertDriveFileOutlined";
import { head } from "lodash";
import moment from "moment";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import AudioRecorder from "../AudioRecorder";
import ConfirmationModal from "../ConfirmationModal";
import useAerosSettingsStyles from "../Settings/aerosSettingsStyles";
import { AEROS, stateText } from "../../styles/aerosTokens";
import {
  mediaKind,
  shortcodeIssue,
  substituteVariables,
  formatBytes,
  MAX_MESSAGE_LENGTH,
} from "../../pages/QuickMessages/quickMessageRules";

const MEDIA_ICONS = {
  image: ImageOutlinedIcon,
  audio: MicNoneIcon,
  video: VideocamOutlinedIcon,
  pdf: PictureAsPdfOutlinedIcon,
  document: InsertDriveFileOutlinedIcon,
};
const MEDIA_COLORS = {
  image: AEROS.cyan,
  audio: AEROS.success,
  video: "#C08BFF",
  pdf: AEROS.error,
  document: AEROS.warning,
};

const QUICK_VARS = [
  { value: "{{name}}", primary: true },
  { value: "{{firstName}}", primary: true },
  { value: "{{userName}}" },
  { value: "{{ms}}" },
  { value: "{{protocol}}" },
];
const ALL_VARS = [
  "{{firstName}}",
  "{{name}}",
  "{{userName}}",
  "{{ms}}",
  "{{protocol}}",
  "{{date}}",
  "{{hour}}",
  "{{ticket_id}}",
  "{{queue}}",
  "{{connection}}",
  "{{dataAgendamento}}",
];

const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  const border = isLight ? theme.palette.divider : "rgba(255,255,255,0.07)";
  const borderInput = isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.1)";
  const purpleText = stateText(theme, "#C08BFF", "purple");
  const cyanText = stateText(theme, AEROS.cyan, "info");
  const mono = "ui-monospace, SFMono-Regular, Menlo, monospace";
  return {
    paper: {
      width: 820,
      maxWidth: "calc(100vw - 32px)",
      maxHeight: "calc(var(--vh-full) - 48px)",
      margin: 16,
    },
    form: { display: "flex", flexDirection: "column", minHeight: 0, maxHeight: "inherit" },
    header: {
      padding: "20px 24px 16px",
      display: "flex",
      alignItems: "center",
      borderBottom: `1px solid ${border}`,
      flexShrink: 0,
    },
    title: {
      fontFamily: AEROS.fontDisplay,
      fontSize: 16,
      fontWeight: 700,
      letterSpacing: "-0.01em",
      color: theme.palette.text.primary,
    },
    subtitle: { fontSize: 12, color: theme.palette.text.secondary, marginTop: 2 },
    closeButton: {
      marginLeft: "auto",
      padding: 4,
      color: theme.palette.aeros.textTertiary,
      "& .MuiSvgIcon-root": { fontSize: 20 },
    },
    body: {
      display: "grid",
      gridTemplateColumns: "minmax(0, 1.4fr) minmax(0, 1fr)",
      minHeight: 0,
      overflowY: "auto",
      ...theme.scrollbarStyles,
      [theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr" },
    },
    formCol: {
      padding: "20px 24px",
      display: "flex",
      flexDirection: "column",
      gap: 16,
      borderRight: `1px solid ${border}`,
      "& > *": { flexShrink: 0 },
      [theme.breakpoints.down("sm")]: { borderRight: "none" },
    },
    fieldLabel: {
      fontSize: 12,
      fontWeight: 600,
      color: theme.palette.text.secondary,
      marginBottom: 6,
      display: "block",
    },
    fieldLabelFocus: { color: cyanText },
    input: {
      width: "100%",
      "& .MuiOutlinedInput-root": {
        borderRadius: 10,
        backgroundColor: theme.palette.aeros.surface,
        fontSize: 13.5,
      },
      "& .MuiOutlinedInput-notchedOutline": { borderColor: borderInput },
      "& .MuiOutlinedInput-input": { padding: "9px 12px" },
      "& .MuiOutlinedInput-adornedStart": { paddingLeft: 12 },
      "& .MuiOutlinedInput-adornedStart .MuiOutlinedInput-input": { paddingLeft: 0 },
      "& .MuiOutlinedInput-multiline": { padding: "12px 14px" },
      "& .MuiOutlinedInput-multiline .MuiOutlinedInput-input": { padding: 0 },
    },
    shortcodeInput: {
      "& .MuiOutlinedInput-input": { fontFamily: mono },
    },
    slash: { color: theme.palette.aeros.textTertiary, fontFamily: mono, marginRight: 6 },
    hint: {
      fontSize: 11,
      color: theme.palette.aeros.textTertiary,
      marginTop: 5,
      display: "flex",
      alignItems: "center",
      gap: 4,
      "& .MuiSvgIcon-root": { fontSize: 13 },
    },
    hintOk: { "& .MuiSvgIcon-root": { color: AEROS.success } },
    hintError: {
      color: stateText(theme, AEROS.error, "error"),
      "& .MuiSvgIcon-root": { color: AEROS.error },
    },
    messageHead: { display: "flex", alignItems: "center", marginBottom: 6 },
    messageTools: { marginLeft: "auto", display: "flex", gap: 6, position: "relative" },
    toolChip: {
      font: "inherit",
      fontSize: 11,
      fontWeight: 600,
      lineHeight: 1.5,
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      color: theme.palette.text.secondary,
      border: `1px solid ${borderInput}`,
      borderRadius: 6,
      padding: "2px 8px",
      background: "transparent",
      cursor: "pointer",
      "& .MuiSvgIcon-root": { fontSize: 13 },
      "&:hover": { color: theme.palette.text.primary },
      "&:disabled": { opacity: 0.5, cursor: "default" },
    },
    toolChipActive: {
      color: purpleText,
      backgroundColor: "rgba(160,68,255,0.12)",
      borderColor: "transparent",
    },
    emojiPopover: {
      position: "absolute",
      right: 0,
      top: "calc(100% + 6px)",
      zIndex: 5,
      "& .emoji-mart": {
        borderRadius: 12,
        border: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.12)"}`,
        boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
      },
    },
    messageInput: {
      "& .MuiOutlinedInput-root": { lineHeight: 1.6 },
    },
    varRow: { display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" },
    varChip: {
      fontSize: 10.5,
      fontWeight: 600,
      fontFamily: mono,
      color: theme.palette.text.secondary,
      border: `1px dashed ${isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.15)"}`,
      borderRadius: 6,
      padding: "2px 9px",
      background: "transparent",
      cursor: "pointer",
      "&:hover": { color: theme.palette.text.primary },
      "&:disabled": { opacity: 0.5, cursor: "default" },
    },
    varChipPrimary: {
      color: cyanText,
      backgroundColor: alpha(AEROS.cyan, 0.08),
      borderColor: alpha(AEROS.cyan, 0.3),
      "&:hover": { color: cyanText },
    },
    // ---- anexo ----
    attachBox: {
      display: "flex",
      alignItems: "center",
      gap: 12,
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${borderInput}`,
      borderRadius: 10,
      padding: "10px 12px",
    },
    attachIcon: {
      width: 38,
      height: 38,
      borderRadius: 9,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flex: "none",
      "& .MuiSvgIcon-root": { fontSize: 20 },
    },
    attachName: {
      fontSize: 13,
      fontWeight: 600,
      color: theme.palette.text.primary,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    attachMeta: { fontSize: 11, color: theme.palette.text.secondary },
    attachEmpty: {
      display: "flex",
      gap: 8,
      flexWrap: "wrap",
    },
    ghostButton: {
      border: `1px solid ${isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.14)"}`,
      borderRadius: 9,
      padding: "6px 12px",
      fontSize: 12,
      fontWeight: 600,
      color: theme.palette.text.primary,
      "& .MuiButton-startIcon": { marginRight: 5 },
      "& .MuiButton-startIcon .MuiSvgIcon-root": { fontSize: 15 },
    },
    iconButton: {
      width: 28,
      height: 28,
      borderRadius: 8,
      border: `1px solid ${theme.palette.divider}`,
      color: theme.palette.text.secondary,
      padding: 0,
      flex: "none",
      "& .MuiSvgIcon-root": { fontSize: 15 },
      "&:hover": { color: AEROS.error, backgroundColor: "transparent" },
    },
    // ---- compartilhar ----
    shareBox: {
      border: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.08)"}`,
      borderRadius: 12,
      padding: "12px 14px",
      display: "flex",
      alignItems: "center",
      gap: 12,
      flexWrap: "wrap",
      "& > .MuiSvgIcon-root": { fontSize: 18, color: purpleText },
    },
    shareTitle: { fontSize: 13, fontWeight: 600, color: theme.palette.text.primary },
    shareDesc: { fontSize: 11.5, color: theme.palette.text.secondary },
    shareSub: {
      width: "100%",
      display: "flex",
      alignItems: "center",
      gap: 10,
      paddingTop: 10,
      borderTop: `1px solid ${border}`,
      fontSize: 12.5,
      color: theme.palette.text.secondary,
    },
    // ---- oficial ----
    oficialBox: {
      border: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.08)"}`,
      borderRadius: 12,
      padding: "12px 14px",
      display: "flex",
      flexDirection: "column",
      gap: 10,
    },
    oficialGrid: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: 10,
    },
    chipNeutral: {
      display: "inline-block",
      fontSize: 10,
      fontWeight: 600,
      color: theme.palette.text.secondary,
      backgroundColor: alpha(theme.palette.text.primary, 0.06),
      borderRadius: 5,
      padding: "1px 7px",
      marginBottom: 4,
    },
    // ---- preview ----
    previewCol: {
      padding: "20px 24px",
      backgroundColor: theme.palette.aeros.sidebar,
      display: "flex",
      flexDirection: "column",
      minWidth: 0,
    },
    previewLabel: {
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "0.1em",
      textTransform: "uppercase",
      color: theme.palette.aeros.textTertiary,
      marginBottom: 12,
    },
    typing: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${borderInput}`,
      borderRadius: 10,
      padding: "8px 12px",
      marginBottom: 14,
      "& > .MuiSvgIcon-root": { fontSize: 16, color: purpleText },
    },
    typingTyped: { fontFamily: mono, fontSize: 12.5, color: cyanText },
    typingRest: { fontSize: 12, color: theme.palette.aeros.textTertiary, fontFamily: mono },
    typingKey: {
      marginLeft: "auto",
      fontSize: 10,
      fontWeight: 600,
      color: theme.palette.text.secondary,
      border: `1px solid ${isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.12)"}`,
      borderRadius: 5,
      padding: "1px 6px",
    },
    chatArea: { flex: 1, display: "flex", flexDirection: "column", gap: 8 },
    bubble: {
      alignSelf: "flex-end",
      maxWidth: "92%",
      minWidth: 120,
      backgroundColor: "rgba(160,68,255,0.16)",
      border: "1px solid rgba(160,68,255,0.3)",
      borderRadius: "14px 14px 4px 14px",
      overflow: "hidden",
    },
    bubbleMedia: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "10px 12px",
      backgroundColor: isLight ? "rgba(15,20,40,0.06)" : "rgba(7,8,13,0.4)",
    },
    bubbleMediaIcon: {
      width: 32,
      height: 32,
      borderRadius: 8,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flex: "none",
      "& .MuiSvgIcon-root": { fontSize: 18 },
    },
    bubbleMediaName: {
      fontSize: 12,
      fontWeight: 600,
      color: theme.palette.text.primary,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    bubbleMediaMeta: { fontSize: 10.5, color: theme.palette.text.secondary },
    bubbleImage: { display: "block", width: "100%", maxHeight: 160, objectFit: "cover" },
    bubbleBody: { padding: "10px 12px" },
    bubbleText: {
      fontSize: 12.5,
      lineHeight: 1.55,
      color: theme.palette.text.primary,
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
    },
    bubbleTextEmpty: { color: theme.palette.aeros.textTertiary, fontStyle: "italic" },
    bubbleMeta: {
      fontSize: 10,
      color: theme.palette.text.secondary,
      textAlign: "right",
      marginTop: 4,
      display: "flex",
      justifyContent: "flex-end",
      alignItems: "center",
      gap: 3,
      "& .MuiSvgIcon-root": { fontSize: 12, color: cyanText },
    },
    previewInfo: {
      fontSize: 11,
      color: theme.palette.aeros.textTertiary,
      marginTop: 12,
      display: "flex",
      alignItems: "center",
      gap: 5,
      "& .MuiSvgIcon-root": { fontSize: 13 },
    },
    // ---- footer ----
    footer: {
      padding: "16px 24px",
      borderTop: `1px solid ${border}`,
      display: "flex",
      alignItems: "center",
      gap: 10,
      flexShrink: 0,
      flexWrap: "wrap",
    },
    counter: {
      fontSize: 11.5,
      color: theme.palette.aeros.textTertiary,
      fontVariantNumeric: "tabular-nums",
    },
    counterOver: { color: stateText(theme, AEROS.error, "error") },
    footerActions: { marginLeft: "auto", display: "flex", gap: 10 },
    cancelButton: {
      border: `1px solid ${isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.16)"}`,
      color: theme.palette.text.primary,
      borderRadius: 10,
      padding: "7px 16px",
      fontWeight: 600,
      fontSize: 13,
    },
    submitButton: {
      borderRadius: 10,
      padding: "7px 18px",
      fontWeight: 700,
      fontSize: 13,
      position: "relative",
      "& .MuiButton-startIcon": { marginRight: 4 },
      "& .MuiSvgIcon-root": { fontSize: 16 },
    },
    buttonProgress: {
      color: AEROS.success,
      position: "absolute",
      top: "50%",
      left: "50%",
      marginTop: -12,
      marginLeft: -12,
    },
  };
});

const QuickeMessageSchema = Yup.object().shape({
  shortcode: Yup.string().required("Obrigatório"),
  message: Yup.string().max(MAX_MESSAGE_LENGTH, "Mensagem muito longa"),
  isOficial: Yup.boolean(),
  components: Yup.array().when("isOficial", {
    is: true,
    then: Yup.array().of(
      Yup.object().shape({
        text: Yup.string().trim().required("Obrigatório"),
      })
    ),
    otherwise: Yup.array().notRequired(),
  }),
});

const QuickMessageDialog = ({ open, onClose, quickemessageId, reload, existing = [] }) => {
  const classes = useStyles();
  const aeros = useAerosSettingsStyles();
  const { user } = useContext(AuthContext);

  const messageInputRef = useRef();

  const initialState = {
    shortcode: "",
    message: "",
    geral: true,
    visao: true,
    isOficial: false,
    status: "",
    language: "",
    category: "",
    metaID: "",
  };

  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [quickemessage, setQuickemessage] = useState(initialState);
  const [attachment, setAttachment] = useState(null);
  const [audioBlob, setAudioBlob] = useState(null);
  const [mediaMode, setMediaMode] = useState(null); // 'file' | 'audio' | null
  const [isEditingMedia, setIsEditingMedia] = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);
  const [showAllVars, setShowAllVars] = useState(false);
  const [shortcodeFocus, setShortcodeFocus] = useState(false);
  const attachmentFile = useRef(null);

  useEffect(() => {
    if (!open) return;
    if (!quickemessageId) {
      setQuickemessage(initialState);
      return;
    }
    (async () => {
      try {
        const { data } = await api.get(`/quick-messages/${quickemessageId}`);
        setQuickemessage((prevState) => ({ ...prevState, ...data }));
        setIsEditingMedia(false);
        setMediaMode(null);
      } catch (err) {
        toastError(err);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quickemessageId, open]);

  const handleClose = () => {
    setQuickemessage(initialState);
    setAttachment(null);
    setAudioBlob(null);
    setMediaMode(null);
    setIsEditingMedia(false);
    setShowEmoji(false);
    setShowAllVars(false);
    onClose();
  };

  const handleAttachmentFile = (e) => {
    const file = head(e.target.files);
    if (file) {
      setAttachment(file);
      setAudioBlob(null);
      setMediaMode("file");
      setIsEditingMedia(true);
    }
  };

  const handleAudioRecorded = (blob) => {
    setAudioBlob(blob);
    setAttachment(null);
    setMediaMode("audio");
    setIsEditingMedia(true);
  };

  const handleAudioDeleted = () => {
    setAudioBlob(null);
    setMediaMode(null);
    setIsEditingMedia(false);
  };

  const clearNewMedia = () => {
    setAttachment(null);
    setAudioBlob(null);
    setMediaMode(null);
    setIsEditingMedia(false);
    if (attachmentFile.current) {
      attachmentFile.current.value = null;
    }
  };

  const getBasename = (filepath) => {
    if (!filepath) return "";
    const cleanPath = filepath.split("?")[0].split("#")[0];
    const segments = cleanPath.split("/");
    return segments[segments.length - 1];
  };

  const handleSaveQuickeMessage = async (values) => {
    const quickemessageData = {
      ...values,
      shortcode: String(values.shortcode || "").replace(/^\//, "").trim(),
      isMedia: !!(attachment || audioBlob || (quickemessage.mediaPath && !isEditingMedia)),
      mediaPath: attachment
        ? String(attachment.name).replace(/ /g, "_")
        : values.mediaPath
        ? getBasename(values.mediaPath).replace(/ /g, "_")
        : null,
      isOficial: quickemessageId ? values.isOficial : false,
    };
    // Campos do template oficial vazios vão como null: "" colide com o
    // índice único parcial (companyId, metaID) a partir da segunda resposta.
    ["status", "language", "category", "metaID"].forEach((k) => {
      if (!quickemessageData[k]) quickemessageData[k] = null;
    });

    if (Array.isArray(values.components)) {
      quickemessageData.components = values.components.map((c) => ({ id: c.id, text: c.text }));
    }

    try {
      let quickMessageRecord;

      if (quickemessageId) {
        quickMessageRecord = await api.put(`/quick-messages/${quickemessageId}`, quickemessageData);
      } else {
        const { data } = await api.post("/quick-messages", quickemessageData);
        quickMessageRecord = { data };
      }

      const messageId = quickemessageId || quickMessageRecord.data.id;

      if (isEditingMedia && quickemessage.mediaPath) {
        await api.delete(`/quick-messages/${messageId}/media-upload`);
      }

      if (attachment) {
        const formData = new FormData();
        formData.append("typeArch", "quickMessage");
        formData.append("file", attachment);
        await api.post(`/quick-messages/${messageId}/media-upload`, formData);
      }

      if (audioBlob) {
        const formData = new FormData();
        formData.append("typeArch", "quickMessage");
        formData.append("audio", audioBlob, `audio_${Date.now()}.webm`);
        await api.post(`/quick-messages/${messageId}/audio-upload`, formData);
      }

      toast.success(i18n.t("quickMessages.toasts.success"));
      if (typeof reload === "function") {
        reload();
      }
    } catch (err) {
      toastError(err);
    }
    handleClose();
  };

  const deleteMedia = async () => {
    if (attachment) {
      setAttachment(null);
      if (attachmentFile.current) attachmentFile.current.value = null;
    }
    if (audioBlob) {
      setAudioBlob(null);
    }
    if (quickemessage.mediaPath) {
      try {
        await api.delete(`/quick-messages/${quickemessage.id}/media-upload`);
        setQuickemessage((prev) => ({ ...prev, mediaPath: null, mediaName: null, mediaType: null }));
        toast.success(i18n.t("quickMessages.toasts.deleted"));
        if (typeof reload === "function") {
          reload();
        }
      } catch (err) {
        toastError(err);
      }
    }
    setMediaMode(null);
    setIsEditingMedia(false);
    setConfirmationOpen(false);
  };

  const insertAtCursor = async (text, setValueFunc, currentValue) => {
    const el = messageInputRef.current;
    if (!el) {
      setValueFunc("message", `${currentValue || ""}${text}`);
      return;
    }
    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? el.value.length;
    const firstHalfText = el.value.substring(0, start);
    const secondHalfText = el.value.substring(end);
    const newCursorPos = start + text.length;

    setValueFunc("message", `${firstHalfText}${text}${secondHalfText}`);

    await new Promise((r) => setTimeout(r, 100));
    if (messageInputRef.current) {
      messageInputRef.current.setSelectionRange(newCursorPos, newCursorPos);
      messageInputRef.current.focus();
    }
  };

  const hasNewMedia = attachment || audioBlob;
  const hasExistingMedia = quickemessage.mediaPath && !isEditingMedia;

  // Descrição unificada do anexo (novo ou existente) para a caixa e a prévia
  const attachmentInfo = useMemo(() => {
    if (attachment) {
      const kind = mediaKind({ mediaType: attachment.type.split("/")[0], mediaName: attachment.name });
      return { kind, name: attachment.name, size: attachment.size, url: null };
    }
    if (audioBlob) {
      return {
        kind: "audio",
        name: i18n.t("quickMessages.dialog.recordedAudio"),
        size: audioBlob.size,
        url: null,
      };
    }
    if (hasExistingMedia) {
      const kind = mediaKind(quickemessage);
      const url = `${process.env.REACT_APP_BACKEND_URL || "http://localhost:8080"}/public/company${user.companyId}/quickMessage/${quickemessage.mediaName}`;
      return { kind, name: quickemessage.mediaName, size: null, url };
    }
    return null;
  }, [attachment, audioBlob, hasExistingMedia, quickemessage, user.companyId]);

  const AttachIcon = attachmentInfo ? MEDIA_ICONS[attachmentInfo.kind] || InsertDriveFileOutlinedIcon : null;
  const attachColor = attachmentInfo ? MEDIA_COLORS[attachmentInfo.kind] || AEROS.warning : null;

  return (
    <>
      <ConfirmationModal
        title={i18n.t("quickMessages.confirmationModal.deleteMediaTitle")}
        open={confirmationOpen}
        onClose={() => setConfirmationOpen(false)}
        onConfirm={deleteMedia}
      >
        {i18n.t("quickMessages.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth={false}
        classes={{ paper: classes.paper }}
        scroll="paper"
      >
        <div style={{ display: "none" }}>
          <input
            type="file"
            accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.txt"
            ref={attachmentFile}
            onChange={handleAttachmentFile}
          />
        </div>
        <Formik
          initialValues={{
            ...quickemessage,
            isStarter: quickemessage?.isStarter || false,
          }}
          enableReinitialize={true}
          validationSchema={QuickeMessageSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveQuickeMessage(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ touched, errors, isSubmitting, setFieldValue, values }) => {
            // Resposta de outro atendente, compartilhada mas sem permissão de edição
            const isDisabled =
              quickemessageId && values.visao && !values.geral && values.userId !== user.id;
            const issue = shortcodeIssue(values.shortcode, existing, quickemessageId || null);
            const shortcodeClean = String(values.shortcode || "").replace(/^\//, "");
            const typed = shortcodeClean.slice(0, 3);
            const rest = shortcodeClean.slice(3);
            const length = (values.message || "").length;
            const preview = substituteVariables(values.message, user?.name);

            return (
              <Form className={classes.form}>
                <div className={classes.header}>
                  <div>
                    <div className={classes.title}>
                      {quickemessageId
                        ? i18n.t("quickMessages.dialog.edit")
                        : i18n.t("quickMessages.dialog.add")}
                    </div>
                    <div className={classes.subtitle}>{i18n.t("quickMessages.dialog.subtitle")}</div>
                  </div>
                  <IconButton className={classes.closeButton} onClick={handleClose} aria-label={i18n.t("quickMessages.buttons.cancel")}>
                    <CloseIcon />
                  </IconButton>
                </div>

                <div className={classes.body}>
                  {/* ---------- Formulário ---------- */}
                  <div className={classes.formCol}>
                    <div>
                      <label
                        className={`${classes.fieldLabel} ${shortcodeFocus ? classes.fieldLabelFocus : ""}`}
                        htmlFor="quick-shortcode"
                      >
                        {i18n.t("quickMessages.dialog.shortcode")}
                      </label>
                      <Field
                        as={TextField}
                        id="quick-shortcode"
                        name="shortcode"
                        autoFocus
                        variant="outlined"
                        className={`${classes.input} ${classes.shortcodeInput}`}
                        disabled={isDisabled}
                        placeholder={i18n.t("quickMessages.dialog.shortcodePlaceholder")}
                        error={touched.shortcode && Boolean(errors.shortcode)}
                        onFocus={() => setShortcodeFocus(true)}
                        onBlur={() => setShortcodeFocus(false)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <span className={classes.slash}>/</span>
                            </InputAdornment>
                          ),
                        }}
                      />
                      {issue && issue !== "empty" ? (
                        <div className={`${classes.hint} ${classes.hintError}`}>
                          <ErrorOutlineIcon />
                          {i18n.t(`quickMessages.dialog.shortcodeIssue.${issue}`)}
                        </div>
                      ) : (
                        <div className={`${classes.hint} ${issue ? "" : classes.hintOk}`}>
                          {issue ? <InfoOutlinedIcon /> : <CheckCircleOutlineIcon />}
                          {i18n.t(issue ? "quickMessages.dialog.shortcodeHint" : "quickMessages.dialog.shortcodeAvailable")}
                        </div>
                      )}
                    </div>

                    <div>
                      <div className={classes.messageHead}>
                        <label className={classes.fieldLabel} style={{ marginBottom: 0 }} htmlFor="quick-message">
                          {i18n.t("quickMessages.dialog.message")}
                        </label>
                        <div className={classes.messageTools}>
                          <button
                            type="button"
                            className={`${classes.toolChip} ${showEmoji ? classes.toolChipActive : ""}`}
                            onClick={() => setShowEmoji((v) => !v)}
                            disabled={isDisabled}
                          >
                            <MoodIcon /> {i18n.t("quickMessages.dialog.emoji")}
                          </button>
                          <button
                            type="button"
                            className={`${classes.toolChip} ${showAllVars ? classes.toolChipActive : ""}`}
                            onClick={() => setShowAllVars((v) => !v)}
                          >
                            <CodeIcon /> {i18n.t("quickMessages.dialog.variables")}
                          </button>
                          {showEmoji && (
                            <ClickAwayListener onClickAway={() => setShowEmoji(false)}>
                              <div className={classes.emojiPopover}>
                                <Picker
                                  perLine={9}
                                  theme="dark"
                                  showPreview={false}
                                  showSkinTones={false}
                                  onSelect={(emoji) => {
                                    insertAtCursor(emoji.native, setFieldValue, values.message);
                                  }}
                                />
                              </div>
                            </ClickAwayListener>
                          )}
                        </div>
                      </div>
                      <Field
                        as={TextField}
                        id="quick-message"
                        name="message"
                        inputRef={messageInputRef}
                        variant="outlined"
                        className={`${classes.input} ${classes.messageInput}`}
                        placeholder={i18n.t("quickMessages.dialog.messagePlaceholder")}
                        error={touched.message && Boolean(errors.message)}
                        helperText={touched.message && errors.message}
                        disabled={isDisabled}
                        multiline
                        minRows={4}
                      />
                      <div className={classes.varRow}>
                        {(showAllVars ? ALL_VARS.map((value) => ({ value })) : QUICK_VARS).map((v) => (
                          <button
                            key={v.value}
                            type="button"
                            className={`${classes.varChip} ${v.primary ? classes.varChipPrimary : ""}`}
                            disabled={isDisabled}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              if (!isSubmitting && !isDisabled) insertAtCursor(v.value, setFieldValue, values.message);
                            }}
                          >
                            {v.value}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className={classes.fieldLabel}>{i18n.t("quickMessages.dialog.attachment")}</label>
                      {attachmentInfo ? (
                        <div className={classes.attachBox}>
                          <div className={classes.attachIcon} style={{ backgroundColor: alpha(attachColor, 0.12) }}>
                            <AttachIcon style={{ color: attachColor }} />
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div className={classes.attachName}>{attachmentInfo.name}</div>
                            <div className={classes.attachMeta}>
                              {attachmentInfo.size != null ? `${formatBytes(attachmentInfo.size)} · ` : ""}
                              {i18n.t(`quickMessages.media.${attachmentInfo.kind}`)}
                              {hasNewMedia ? ` · ${i18n.t("quickMessages.dialog.newFile")}` : ""}
                            </div>
                          </div>
                          <IconButton
                            className={classes.iconButton}
                            disabled={isDisabled}
                            onClick={() => (hasNewMedia ? clearNewMedia() : setConfirmationOpen(true))}
                            aria-label={i18n.t("quickMessages.dialog.removeAttachment")}
                          >
                            <CloseIcon />
                          </IconButton>
                        </div>
                      ) : mediaMode === "audio" ? (
                        <div className={classes.attachBox} style={{ flexDirection: "column", alignItems: "stretch" }}>
                          <AudioRecorder
                            onAudioRecorded={handleAudioRecorded}
                            onAudioDeleted={handleAudioDeleted}
                            disabled={isSubmitting || isDisabled}
                          />
                          <Button variant="text" className={classes.ghostButton} onClick={() => setMediaMode(null)}>
                            {i18n.t("quickMessages.buttons.cancel")}
                          </Button>
                        </div>
                      ) : (
                        <div className={classes.attachEmpty}>
                          <Button
                            variant="text"
                            className={classes.ghostButton}
                            startIcon={<AttachFileIcon />}
                            onClick={() => attachmentFile.current.click()}
                            disabled={isSubmitting || isDisabled}
                          >
                            {i18n.t("quickMessages.buttons.attach")}
                          </Button>
                          <Button
                            variant="text"
                            className={classes.ghostButton}
                            startIcon={<MicIcon />}
                            onClick={() => setMediaMode("audio")}
                            disabled={isSubmitting || isDisabled}
                          >
                            {i18n.t("quickMessages.dialog.recordAudio")}
                          </Button>
                        </div>
                      )}
                      <div className={classes.hint}>{i18n.t("quickMessages.dialog.attachmentHint")}</div>
                    </div>

                    <div className={classes.shareBox}>
                      <GroupOutlinedIcon />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className={classes.shareTitle}>{i18n.t("quickMessages.dialog.shareTitle")}</div>
                        <div className={classes.shareDesc}>
                          {i18n.t(values.visao ? "quickMessages.dialog.shareOn" : "quickMessages.dialog.shareOff")}
                        </div>
                      </div>
                      <Switch
                        className={aeros.toggleSwitch}
                        checked={Boolean(values.visao)}
                        onChange={(e) => setFieldValue("visao", e.target.checked)}
                        disabled={isDisabled}
                        color="primary"
                        inputProps={{ "aria-label": i18n.t("quickMessages.dialog.shareTitle") }}
                      />
                      {values.visao && (
                        <div className={classes.shareSub}>
                          <Switch
                            className={aeros.toggleSwitchSmall}
                            checked={Boolean(values.geral)}
                            onChange={(e) => setFieldValue("geral", e.target.checked)}
                            disabled={isDisabled}
                            color="primary"
                          />
                          {i18n.t("quickMessages.dialog.allowEdit")}
                        </div>
                      )}
                    </div>

                    {values.isOficial && (
                      <div className={classes.oficialBox}>
                        <div className={classes.shareTitle}>{i18n.t("quickMessages.dialog.oficialTitle")}</div>
                        <FormControlLabel
                          control={<Field name="isStarter" type="checkbox" as={Checkbox} color="primary" />}
                          label={i18n.t("quickMessages.dialog.isStarter")}
                        />
                        {(values.components || []).map((comp, idx) => (
                          <div key={comp.id || idx}>
                            <span className={classes.chipNeutral}>{comp.type || "component"}</span>
                            <Field
                              as={TextField}
                              name={`components.${idx}.text`}
                              variant="outlined"
                              className={classes.input}
                              multiline
                              error={Boolean(
                                getIn(touched, `components.${idx}.text`) && getIn(errors, `components.${idx}.text`)
                              )}
                              helperText={
                                getIn(touched, `components.${idx}.text`) && getIn(errors, `components.${idx}.text`)
                              }
                            />
                          </div>
                        ))}
                        <div className={classes.oficialGrid}>
                          {["status", "language", "category", "metaID"].map((name) => (
                            <div key={name}>
                              <label className={classes.fieldLabel}>{i18n.t(`quickMessages.dialog.${name}`)}</label>
                              <Field as={TextField} name={name} variant="outlined" className={classes.input} disabled />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* ---------- Pré-visualização ---------- */}
                  <div className={classes.previewCol}>
                    <div className={classes.previewLabel}>{i18n.t("quickMessages.dialog.previewTitle")}</div>
                    <div className={classes.typing}>
                      <FlashOnIcon />
                      <span className={classes.typingTyped}>/{typed}</span>
                      <span className={classes.typingRest}>{rest}</span>
                      <span className={classes.typingKey}>Tab</span>
                    </div>
                    <div className={classes.chatArea}>
                      <div className={classes.bubble}>
                        {attachmentInfo && attachmentInfo.kind === "image" && attachmentInfo.url && (
                          <img src={attachmentInfo.url} alt={attachmentInfo.name} className={classes.bubbleImage} />
                        )}
                        {attachmentInfo && !(attachmentInfo.kind === "image" && attachmentInfo.url) && (
                          <div className={classes.bubbleMedia}>
                            <div className={classes.bubbleMediaIcon} style={{ backgroundColor: alpha(attachColor, 0.15) }}>
                              <AttachIcon style={{ color: attachColor }} />
                            </div>
                            <div style={{ minWidth: 0 }}>
                              <div className={classes.bubbleMediaName}>{attachmentInfo.name}</div>
                              {attachmentInfo.size != null && (
                                <div className={classes.bubbleMediaMeta}>{formatBytes(attachmentInfo.size)}</div>
                              )}
                            </div>
                          </div>
                        )}
                        <div className={classes.bubbleBody}>
                          <div className={`${classes.bubbleText} ${!preview ? classes.bubbleTextEmpty : ""}`}>
                            {preview || i18n.t("quickMessages.dialog.previewEmpty")}
                          </div>
                          <div className={classes.bubbleMeta}>
                            {moment().format("HH:mm")} · {user?.name} <DoneAllIcon />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className={classes.previewInfo}>
                      <InfoOutlinedIcon />
                      {i18n.t("quickMessages.dialog.previewInfo")}
                    </div>
                  </div>
                </div>

                <div className={classes.footer}>
                  <div className={`${classes.counter} ${length > MAX_MESSAGE_LENGTH ? classes.counterOver : ""}`}>
                    {length.toLocaleString("pt-BR")} / {MAX_MESSAGE_LENGTH.toLocaleString("pt-BR")}{" "}
                    {i18n.t("quickMessages.dialog.characters")}
                  </div>
                  <div className={classes.footerActions}>
                    <Button onClick={handleClose} disabled={isSubmitting} variant="outlined" className={classes.cancelButton}>
                      {i18n.t("quickMessages.buttons.cancel")}
                    </Button>
                    <Button
                      type="submit"
                      color="primary"
                      disabled={isSubmitting || isDisabled || Boolean(issue)}
                      variant="contained"
                      className={classes.submitButton}
                      startIcon={<FlashOnIcon />}
                    >
                      {i18n.t("quickMessages.buttons.save")}
                      {isSubmitting && <CircularProgress size={24} className={classes.buttonProgress} />}
                    </Button>
                  </div>
                </div>
              </Form>
            );
          }}
        </Formik>
      </Dialog>
    </>
  );
};

export default QuickMessageDialog;
